/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
  isEmpty,
} from '../../../../../../_metronic/helpers'
import {Templo} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import { getTemples } from './_requests'

const QueryResponseContext = createResponseContext<Templo>(initialQueryResponse)
const QueryResponseTemplosProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.TEMPLES_LIST}-${query}`,
    () => {
      // permite listar os resultados somente a partir do campo de busca preenchido
      if (isEmpty(query)){
        return Promise.reject();
      }
      return getTemples(query)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseContext.Provider value={{ isLoading: isEmpty(query) ? false : isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return response?.data || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    page: 0,
    rowsperpage: 10,
    total: 0,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  const { page = 0, total = 0, rowsperpage = 10 } = response.payload.pagination
  const totalPages = Math.ceil((total || 0) / (rowsperpage || 10))

  // Gera links inteligentes: mostra apenas páginas próximas à atual
  const generatePageLinks = () => {
    const maxLinks = 7 // Máximo de links numéricos a exibir
    const sideButtons = 2 // Quantas páginas antes/depois da atual

    if (totalPages <= maxLinks) {
      // Se total de páginas é pequeno, mostra todas
      return Array.from({ length: totalPages }, (_, i) => ({
        label: String(i + 1),
        active: i === page,
        url: String(i),
        page: i,
      }))
    }

    const pageLinks = []
    const start = Math.max(0, page - sideButtons)
    const end = Math.min(totalPages - 1, page + sideButtons)

    // Sempre incluir primeira página
    if (start > 0) {
      pageLinks.push({
        label: '1',
        active: false,
        url: '0',
        page: 0,
      })
    }

    // Adicionar "..." se houver gap
    if (start > 1) {
      pageLinks.push({
        label: '...',
        active: false,
        url: null,
        page: null,
      })
    }

    // Adicionar páginas ao redor da página atual
    for (let i = start; i <= end; i++) {
      pageLinks.push({
        label: String(i + 1),
        active: i === page,
        url: String(i),
        page: i,
      })
    }

    // Adicionar "..." se houver gap
    if (end < totalPages - 2) {
      pageLinks.push({
        label: '...',
        active: false,
        url: null,
        page: null,
      })
    }

    // Sempre incluir última página
    if (end < totalPages - 1) {
      pageLinks.push({
        label: String(totalPages),
        active: false,
        url: String(totalPages - 1),
        page: totalPages - 1,
      })
    }

    return pageLinks
  }

  const links = [
    { label: '&laquo; Anterior', active: false, url: null, page: page > 0 ? page - 1 : null },
    ...generatePageLinks(),
    { label: 'Próximo &raquo;', active: false, url: null, page: page < totalPages - 1 ? page + 1 : null },
  ]

  return { page, total, rowsperpage, links }
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseTemplosProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
